import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useAuthRedirect from '../../hooks/useAuthRedirect';
import { getErrorMessage } from './authError';
import PasswordField from './PasswordField';

// Rule-based password strength (no zxcvbn). Returns a 0-4 score and a label.
const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];

const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '' };
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
  let score = 0;
  if (pw.length >= 10) score += 1;
  if (pw.length >= 14) score += 1;
  if (classes >= 2) score += 1;
  if (classes >= 3) score += 1;
  score = Math.min(score, 4);
  return { score, label: STRENGTH_LABELS[score] };
};

const describeComposition = (pw) => {
  const parts = [];
  if (/[A-Za-z]/.test(pw)) parts.push('letters');
  if (/\d/.test(pw)) parts.push('numbers');
  if (/[^A-Za-z0-9]/.test(pw)) parts.push('a symbol');
  if (!parts.length) return '';
  if (parts.length === 1) return ` with ${parts[0]}`;
  return ` with ${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const MIN_LENGTH = 10;

/**
 * Shared "set a password from a token" form used by both /reset-password and
 * /accept-invite. Only copy and the dispatched thunk differ.
 *
 * Props:
 *  - thunk            redux thunk to dispatch ({ token, password }) -> { token, data }
 *  - side             { eyebrow, headline, paragraph } for the left pitch panel
 *  - bracket          eyebrow chip text, e.g. "[ Security · Password reset ]"
 *  - headline         right-panel H1
 *  - lede             right-panel intro paragraph
 *  - ctaLabel         primary button label
 *  - requestNewLinkTo route for the "Request a new link" CTA in the error state
 *  - errorHeadline    error-state H1 (expired/used/missing token)
 *  - errorBody        error-state paragraph
 */
export default function SetPasswordForm({
  thunk,
  side,
  bracket,
  headline,
  lede,
  ctaLabel,
  requestNewLinkTo,
  errorHeadline,
  errorBody,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading } = useAuthRedirect('/sites', '/login');

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState('');
  const [apiLinkError, setApiLinkError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Missing token, or a 410 EXPIRED / 400 INVALID response, both render the
  // same invalid-link state. We never call the API when the token is absent.
  const showLinkError = !token || apiLinkError;

  const strength = getStrength(password);
  const strengthHint = password
    ? `${strength.label} · ${password.length} characters${describeComposition(password)}.`
    : `Use at least ${MIN_LENGTH} characters with a mix of letters, numbers and a symbol.`;

  const confirmTouched = confirm.length > 0;
  const passwordsMatch = password === confirm;
  const rulesPass = password.length >= MIN_LENGTH && passwordsMatch;
  const busy = submitting || isLoading;

  const onSubmit = async (event) => {
    event.preventDefault();
    setLocalError('');

    if (!token) return;
    if (password.length < MIN_LENGTH) {
      setLocalError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setLocalError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(thunk({ token, password })).unwrap();
      navigate('/sites');
    } catch (err) {
      const code = err?.code;
      if (code === 'EXPIRED' || code === 'INVALID') {
        setApiLinkError(true);
      } else {
        setLocalError(getErrorMessage(err, 'Could not set your password. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <section className="auth-side">
        <div className="auth-brand">
          <img src="/nc-logo-white.svg" alt="North Commerce" />
        </div>
        <div className="auth-pitch">
          <span className="auth-eyebrow">{side.eyebrow}</span>
          <h1>{side.headline}</h1>
          <p>{side.paragraph}</p>
          {email ? <p className="auth-account-email">Setting the password for {email}.</p> : null}
        </div>
      </section>

      <main className="auth-form-panel">
        <div className="auth-meta">
          <span className="auth-verified">LINK VERIFIED</span>
        </div>

        <div className="auth-form-wrap">
          {showLinkError ? (
            <>
              <span className="auth-bracket">{bracket}</span>
              <h2>{errorHeadline}</h2>
              <p>{errorBody}</p>
              <div className="auth-form-body">
                <Link className="btn btn-primary full-width" to={requestNewLinkTo}>
                  Request a new link →
                </Link>
                <div className="auth-legal">
                  Changed your mind? <Link to="/login">Back to sign in</Link>.
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="auth-bracket">{bracket}</span>
              <h2>{headline}</h2>
              <p>{lede}</p>

              <form className="auth-form-body" onSubmit={onSubmit}>
                {localError ? <div className="error-banner">{localError}</div> : null}

                <PasswordField
                  id="new-password"
                  label="New password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                >
                  <div className="pw-strength" aria-hidden="true">
                    <div className="pw-strength-track">
                      {[0, 1, 2, 3].map((i) => (
                        <span
                          key={i}
                          className={`pw-strength-seg${i < strength.score ? ' is-filled' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="hint">{strengthHint}</span>
                </PasswordField>

                <PasswordField
                  id="confirm-password"
                  label="Confirm new password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  autoComplete="new-password"
                >
                  {confirmTouched ? (
                    passwordsMatch ? (
                      <span className="pw-match is-ok">✓ Passwords match.</span>
                    ) : (
                      <span className="pw-match is-bad">Passwords don’t match yet.</span>
                    )
                  ) : null}
                </PasswordField>

                <button
                  className="btn btn-primary full-width"
                  type="submit"
                  disabled={busy || !rulesPass}
                >
                  {busy ? 'Saving…' : ctaLabel}
                </button>

                <div className="auth-legal">
                  Minimum {MIN_LENGTH} characters. You’ll be signed in once it’s set.
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
